import React, { useEffect, useRef, useState } from "react";

import { GridColumnMenuItemGroup, GridColumnMenuItem, GridColumnMenuItemContent } from "@progress/kendo-react-grid";
import { GridColumnMenuSort, GridColumnMenuFilter } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import ButtonComponent from "../buttons/Button";

import { ColumnFlagParser } from "../utils/helper";
import PropTypes from "prop-types";
import "./ColumnMenu.scss";

/**
 * Column menu component
 * @module
 * @param {array} columns - Columns array
 * @param {bool} filterable - Filterable
 * @param {func} onColumnsSubmit - Function invoked on submit
 * @param {func} onCloseMenu - Function invoked on column menu close
 * @param {func} onSwitchFilterable - On switch filterable function
 */

const ColumnMenu = (props) => {
  const { column, columns, columnItem, compactColumnMenu, filter, filterCell, filterable, onCloseMenu, onColumnsSubmit, onFilterChange, onSwitchFilterable, onColumnLocked } = props;

  const [state, setState] = useState({
    columns: columns?.map((col) => {
      const cfp = new ColumnFlagParser(col.iMriez);
      return { ...col, lNoCheck: cfp.lNoCheck };
    }).sort((a, b) => a.title.localeCompare(b.title)) || [],
    columnsExpanded: true,
    filterable: filterable,
    existingFilters: filter.filters || [],
    filter: filter.filters.find((filter) => filter.field === column.field) || {},
    event: null,
    callCustomSubmit: false
  });

  // Ref to track if the component has mounted
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      if (state.callCustomSubmit) {
        if (state.existingFilters && state.event) {
          onFilterChange({ filters: state.existingFilters }, state.event);
          setState(prev => ({ ...prev, callCustomSubmit: false }));
        }
      }
    }
  }, [state.existingFilters, onFilterChange, state.callCustomSubmit, state.event]);

  const onToggleColumn = (filed) => {
    setState(prev => ({
      ...prev,
      columns: prev.columns.map((column) => {
        return column.field === filed ? { ...column, disabled: !column.disabled, locked: false } : column;
      }),
    }));
  };

  const onReset = (event) => {
    event.preventDefault();
    const allColumns = columns.map((col) => {
      return {
        ...col,
        disabled: col?.hidden ?? false,
      };
    });

    setState(prev => ({ ...prev, columns: allColumns }));
    onSubmit(event, allColumns);
  };

  const onSubmit = (event, columns) => {
    if (event) {
      event.preventDefault();
    }
    onColumnsSubmit(columns || state.columns);
    if (onCloseMenu) {
      onCloseMenu();
    }
  };

  const onMenuItemClick = () => {
    const value = !state.columnsExpanded;
    setState(prev => ({
      ...prev,
      columnsExpanded: value,
    }));
  };

  const formatFilter = (kendoDefaultFilter) => {
    const result = [];

    const flatten = (arr) => {
      arr.forEach(item => {
        if (item.filters && Array.isArray(item.filters)) {
          flatten(item.filters); // Recursively flatten nested filters
        } else {
          result.push(item);
        }
      });
    };

    if (Array.isArray(kendoDefaultFilter)) {
      flatten(kendoDefaultFilter);
    } else if (kendoDefaultFilter && typeof kendoDefaultFilter === 'object' && kendoDefaultFilter.filters) {
      flatten(kendoDefaultFilter.filters);
    }

    return { filters: result };
  };

  const formatFilterToDefaultKendo = (formattedFilter) => {
    if (!formattedFilter || !Array.isArray(formattedFilter.filters)) {
      return formattedFilter;
    }

    // Wrap each existing filter in the default structure
    const newFilters = formattedFilter.filters.map(filter => ({
      logic: 'and',
      filters: [filter]
    }));

    return {
      filters: newFilters,
      logic: 'and'
    };
  };

  const onCustomFilterChange = (newFilter) => {
    setState(prevState => ({
      ...prevState,
      filter: {
        value: newFilter.value,
        operator: newFilter.nativeEvent?.target?.innerHTML === 'dnes' && prevState.filter?.operator ? prevState.filter.operator : newFilter.operator,
        field: column.field
      },
      event: newFilter.syntheticEvent ? newFilter.syntheticEvent : new Event('change'),
    }));
  };

  const onCustomFilterCancel = () => {
    const updatedFilters = filter.filters.filter(
      filter => filter.field !== column.field
    );

    onFilterChange({ filters: updatedFilters }, { nativeEvent: new Event('reset') });
  };

  const onFilterSubmit = (newFilter, e) => {
    const formatedFilter = formatFilter(newFilter);

    if (e) {
      // kendo default filter submit
      onFilterChange(formatedFilter, e);
    } else {
      // custom filter submit
      let updatedFilters;

      setState(prevState => {
        const existingFilterIndex = prevState.existingFilters?.findIndex(filter => filter?.field === prevState.filter?.field);

        if (prevState.existingFilters) {
          if (existingFilterIndex !== -1) {
            updatedFilters = [...prevState.existingFilters];
            updatedFilters[existingFilterIndex] = prevState.filter;
          } else {
            updatedFilters = [...prevState.existingFilters, prevState.filter];
          }
        } else {
          updatedFilters = [prevState.filter];
        }

        return { ...prevState, existingFilters: updatedFilters, callCustomSubmit: true };
      });
    }
  };

  const isFilterValid = state.filter && ((state.filter.value ?? '') !== '' || ['isnull', 'isnotnull'].includes(state.filter.operator));
  const buildCompactColumnMenu = () => {
    const Filter = filterCell;

    return (
      <div className='k-grid-compactColumnMenu'>
        {columnItem.sortable && <GridColumnMenuSort {...props} />}
        <GridColumnMenuItemGroup>
          <GridColumnMenuItemContent show={!columnItem.hiddenLocked}>
            <div className={"k-column-list-wrapper"}>
              <div className={"k-column-list"}>
                <div
                  className={`k-column-list-item`}
                  onClick={() => onColumnLocked(columnItem.field, !columnItem.locked)}
                >
                  <span className={`k-icon k-font-icon ${columnItem.locked ? "k-i-unlock" : "k-i-lock"}`} />
                  {columnItem.locked ? "Zrušiť ukotvenie" : "Ukotviť"}
                </div>
              </div>
            </div>
          </GridColumnMenuItemContent>
        </GridColumnMenuItemGroup>
        {columnItem.filterable && (
          Filter ? (
            <GridColumnMenuItemGroup>
              <div className="k-expander">
                <GridColumnMenuItem
                  title={"Filter"}
                  iconClass={"filter"}
                  onClick={onMenuItemClick}
                  expandable={true}
                  expanded={state.columnsExpanded}
                />
              </div>
              <GridColumnMenuItemContent show={state.columnsExpanded}>
                <div className='customMenuItem k-filter-menu'>
                  <Filter
                    {...props}
                    field={column.field}
                    value={state.filter?.value}
                    onChange={onCustomFilterChange}
                    operator={state.filter?.operator}
                  />
                  <div className='k-actions k-actions-stretched approveButtons'>
                    <Button
                      className="k-rounded-md filterButton"
                      label="Filter"
                      themeColor={"primary"}
                      disabled={!isFilterValid}
                      onClick={onFilterSubmit}
                    >Filter</Button>
                    <Button
                      className="k-rounded-md cancelButton"
                      label="Zrušiť"
                      onClick={onCustomFilterCancel}
                    >Zrušiť</Button>
                  </div>
                </div>
              </GridColumnMenuItemContent>
            </GridColumnMenuItemGroup>
          ) : (
            <GridColumnMenuFilter
              {...props}
              onFilterChange={onFilterSubmit}
              onExpandChange={onMenuItemClick}
              filter={formatFilterToDefaultKendo(filter)}
              expanded={state.columnsExpanded}
              hideSecondFilter={true}
            />
          )
        )}
      </div>
    );
  };

  const oneVisibleColumn = !compactColumnMenu ? state.columns?.filter((c) => !c.disabled && !c.hidden).length === 1 : null;

  return (
    <div>
      {compactColumnMenu ?
        buildCompactColumnMenu()
        :
        <div className='gridMenu'>
          <GridColumnMenuItemGroup>
            <GridColumnMenuItem title={"Stĺpce"} iconClass={"k-i-columns"} onClick={onMenuItemClick} />
            <GridColumnMenuItemContent show={state.columnsExpanded}>
              <div className={"k-column-list-wrapper"}>
                <form onSubmit={onSubmit} onReset={onReset}>
                  <div className={"k-column-list"}>
                    {state.columns?.map((column) => {
                      if (column.hidden || column.filter === "none") {
                        return null;
                      }

                      return (
                        <div key={`col_${column.field}`} className={"k-column-list-item"}>
                          <span>
                            <input
                              id={`column-visiblity-show-${column.field}`}
                              className="k-checkbox"
                              type="checkbox"
                              readOnly={true}
                              disabled={column.lNoCheck || (!column.disabled && oneVisibleColumn)}
                              checked={!column.disabled}
                              onClick={() => {
                                onToggleColumn(column.field);
                              }}
                            />
                            <label htmlFor={`column-visiblity-show-${column.field}`} className="k-checkbox-label" style={{ userSelect: "none" }}>
                              {column.title}
                            </label>
                          </span>
                          {!column.disabled && !column.hiddenLocked &&
                            <ButtonComponent
                              icon={column.locked ? "lock" : "unlock"}
                              title={column.locked ? "Zrušiť ukotvenie" : "Ukotviť"}
                              onClick={() => onColumnLocked(column.field, !column.locked)}
                            />
                          }
                        </div>
                      );
                    })}
                  </div>
                  <div className={"k-columnmenu-actions"}>
                    <ButtonComponent type={"reset"} className={""} label={"Všetko"} />
                    <ButtonComponent type={"submit"} className={""} primary={true} label={"OK"} />
                  </div>
                </form>
              </div>
            </GridColumnMenuItemContent>
            <GridColumnMenuItem title={"Zobraziť/skryť filter"} iconClass={state.filterable ? "k-i-checkbox-checked" : "k-i-checkbox"} onClick={onSwitchFilterable} />
          </GridColumnMenuItemGroup>
        </div>
      }
    </div>
  );
};

ColumnMenu.propTypes = {
  columns: PropTypes.array,
  filterable: PropTypes.bool,
  onColumnsSubmit: PropTypes.func,
  onCloseMenu: PropTypes.func,
  onSwitchFilterable: PropTypes.func,
};

export default ColumnMenu;